import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ComponentLayoutThumbnail.module.css';
import { getSectionColor, THUMBNAIL_SECTION_ORDER } from './sectionColors';
import { useDraggablePosition } from './useDraggablePosition';

export interface ExpectedComponent {
  id: string;
  name: string;
  section: string;
}

interface TypeStat {
  expected: number;
  name: string;
  rendered: number;
}

interface SectionStat {
  missing: number;
  section: string;
  types: TypeStat[];
}

interface ComponentLayoutThumbnailProps {
  expectedLayout: ExpectedComponent[];
}

// Count actually-rendered components in the live DOM, keyed by `${section}|${name}`.
// renderComponents() tags every successfully-rendered component with these data attributes,
// so a component present in the template but absent here failed to render (timeout, error,
// invalid dehydrated state, etc).
function measureRenderedCounts(): Map<string, number> {
  const counts = new Map<string, number>();
  document.querySelectorAll<HTMLElement>('[data-overlay-name]').forEach(el => {
    const name = el.dataset.overlayName ?? '';
    const section = el.dataset.overlaySection ?? '';
    const key = `${section}|${name}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return counts;
}

function buildSectionStats(expectedLayout: ExpectedComponent[], renderedCounts: Map<string, number>): SectionStat[] {
  // expected[section][name] = count
  const expectedBySection = new Map<string, Map<string, number>>();
  for (const { name, section } of expectedLayout) {
    const byName = expectedBySection.get(section) ?? new Map<string, number>();
    byName.set(name, (byName.get(name) ?? 0) + 1);
    expectedBySection.set(section, byName);
  }

  // Stable ordering: known sections first, then any unexpected ones.
  const orderedSections = [
    ...THUMBNAIL_SECTION_ORDER.filter(s => expectedBySection.has(s)),
    ...[...expectedBySection.keys()].filter(s => !THUMBNAIL_SECTION_ORDER.includes(s as never)),
  ];

  return orderedSections.map(section => {
    const byName = expectedBySection.get(section)!;
    const types: TypeStat[] = [...byName.entries()]
      .map(([name, expected]) => ({
        name,
        expected,
        rendered: Math.min(renderedCounts.get(`${section}|${name}`) ?? 0, expected),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const missing = types.reduce((sum, t) => sum + (t.expected - t.rendered), 0);
    return { section, types, missing };
  });
}

export function ComponentLayoutThumbnail({ expectedLayout }: ComponentLayoutThumbnailProps) {
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sectionStats, setSectionStats] = useState<SectionStat[]>([]);
  const rafRef = useRef<number | null>(null);
  const { containerRef, dragHandlers, style, wasDragged } = useDraggablePosition('componentLayoutThumbnail:pos');

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setSectionStats(buildSectionStats(expectedLayout, measureRenderedCounts()));
    });
  }, [expectedLayout]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    scheduleUpdate();

    // Debounced MutationObserver to catch async/lazy hydration that fills in (or never fills in)
    // late-rendering components.
    let mutationDebounce: ReturnType<typeof setTimeout> | null = null;
    const debouncedScheduleUpdate = () => {
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(scheduleUpdate, 150);
    };

    const mutationObserver = new MutationObserver(debouncedScheduleUpdate);
    const wrapper = document.getElementById('dynamic-page-main-tag-wrapper');
    if (wrapper) {
      mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: false });
    }

    return () => {
      mutationObserver.disconnect();
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, scheduleUpdate]);

  if (!mounted || expectedLayout.length === 0) return null;

  const totalExpected = sectionStats.reduce((sum, s) => sum + s.types.reduce((n, t) => n + t.expected, 0), 0);
  const totalMissing = sectionStats.reduce((sum, s) => sum + s.missing, 0);

  return (
    <div className={styles.thumbnail} ref={containerRef} style={style}>
      <button
        className={styles.header}
        onClick={() => {
          if (wasDragged()) return;
          setCollapsed(c => !c);
        }}
        title="Drag to move. Click to collapse. Expected page layout from the data-templates API. Components shown here but missing from the page failed to render."
        type="button"
        {...dragHandlers}
      >
        <span className={styles.title}>Template layout</span>
        <span className={totalMissing > 0 ? styles.statBad : styles.statOk}>
          {totalMissing > 0 ? `${totalMissing} missing` : 'all rendered'}
        </span>
        <span className={styles.statMuted}>{totalExpected} expected</span>
        <span className={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          {sectionStats.map(({ missing, section, types }) => {
            const color = getSectionColor(section);
            return (
              <div className={styles.section} key={section}>
                <div className={styles.sectionHeader} style={{ backgroundColor: color }}>
                  <span className={styles.sectionLabel}>{section}</span>
                  {missing > 0 && <span className={styles.sectionMissing}>{missing} missing</span>}
                </div>
                <div className={styles.chips}>
                  {types.map(({ expected, name, rendered }) => {
                    const isMissing = rendered < expected;
                    return (
                      <div
                        className={isMissing ? styles.chipMissing : styles.chip}
                        key={name}
                        title={
                          isMissing
                            ? `${name}: ${rendered}/${expected} rendered — ${expected - rendered} failed to render`
                            : `${name}: ${rendered}/${expected} rendered`
                        }
                      >
                        <span className={styles.chipName}>{name}</span>
                        <span className={styles.chipCount}>
                          {rendered}/{expected}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
